// src/pages/admin/finance/AdminTransactions.jsx
import { useEffect, useState, useMemo } from "react";
import {
  fetchAllTransactions,
  fetchStoreDetailByTransaction,
} from "../../../api/adminFinance";
import dayjs from "dayjs";
import jalaliday from "jalaliday";
// import "../../../styles/AdminTransactions.css";
import AdminPendingStores from "../components/AdminPendingStores.jsx";
import "../../../styles/AdminPanel/AdminTransactions.css";

// آیکون لینک خارجی به‌صورت SVG داخلی (بدون نیاز به react-icons)
const ExternalLinkIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

// آیکون چشم برای دکمه‌ی «مشاهده جزئیات»
const EyeIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

dayjs.extend(jalaliday);

// helper برای نمایش متن و کلاس نوع پرداخت (تا تکرار کد نباشه)
const getPayTypeLabel = (type) =>
  type === "gateway"
    ? "درگاه آنلاین"
    : type === "card_to_card"
      ? "کارت به کارت"
      : "نامشخص";

const getPayTypeClass = (type) =>
  type === "gateway"
    ? "admintra-paytype-gateway"
    : type === "card_to_card"
      ? "admintra-paytype-card"
      : "admintra-paytype-unknown";

const toFa = (n) => Number(n || 0).toLocaleString("fa-IR");

export default function AdminTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [storeDetail, setStoreDetail] = useState(null);
  const [selectedTx, setSelectedTx] = useState(null); // ← تراکنش انتخاب‌شده
  const [modalOpen, setModalOpen] = useState(false);
  const [yearFilter, setYearFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 9;
  const [activeTab, setActiveTab] = useState("transactions");

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetchAllTransactions();
        setTransactions(res.data);
      } catch (err) {
        console.error(err);
        setError("خطا در بارگذاری تراکنش‌ها");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const getJalaliYear = (date) => dayjs(date).calendar("jalali").format("YYYY");
  const getJalaliMonth = (date) => dayjs(date).calendar("jalali").format("MM");

  const years = [
    ...new Set(transactions.map((t) => getJalaliYear(t.created_at))),
  ];

  const filteredTransactions = useMemo(() => {
    return transactions.filter((t) => {
      if (yearFilter !== "all" && getJalaliYear(t.created_at) !== yearFilter)
        return false;
      if (monthFilter !== "all" && getJalaliMonth(t.created_at) !== monthFilter)
        return false;
      return true;
    });
  }, [transactions, yearFilter, monthFilter]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredTransactions.length / pageSize),
  );
  const pageSafe = Math.min(currentPage, totalPages);

  useEffect(() => {
    if (currentPage !== pageSafe) setCurrentPage(pageSafe);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTransactions.length]);

  const paginatedTransactions = filteredTransactions.slice(
    (pageSafe - 1) * pageSize,
    pageSafe * pageSize,
  );

  const pageNumbers = useMemo(() => {
    const nums = [];
    for (let n = 1; n <= totalPages; n++) nums.push(n);
    return nums;
  }, [totalPages]);

  const totalSuccessAmount = filteredTransactions
    .filter((t) => t.success)
    .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);

  // ← حالا کل آبجکت تراکنش رو می‌گیره و نگه می‌داره
  const handleShowDetails = async (transaction) => {
    try {
      const res = await fetchStoreDetailByTransaction(transaction.id);
      setStoreDetail(res.data);
      setSelectedTx(transaction);
      setModalOpen(true);
    } catch (err) {
      console.error(err);
      alert("مشکلی در دریافت اطلاعات فروشگاه رخ داد!");
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setStoreDetail(null);
    setSelectedTx(null);
  };

  if (loading) return <div className="admintra-page">در حال بارگذاری...</div>;
  if (error) return <div className="admintra-page admintra-error">{error}</div>;

  return (
    <div className="admintra-page">
      {/* Header: title on the right, tabs on the left */}
      <div className="admintra-header">
        <h1 className="admintra-title">واریزی‌ها</h1>

        <div className="admintra-tabs">
          <button
            className={`admintra-tab ${activeTab === "transactions" ? "admintra-tab--active" : ""}`}
            onClick={() => setActiveTab("transactions")}
          >
            تراکنش‌ها
          </button>
          <button
            className={`admintra-tab ${activeTab === "pending" ? "admintra-tab--active" : ""}`}
            onClick={() => setActiveTab("pending")}
          >
            درخواست‌های موقت
          </button>
        </div>
      </div>

      {activeTab === "pending" ? (
        <AdminPendingStores />
      ) : (
        <>
          <div className="admintra-top-row">
            <div className="admintra-total-box">
              <strong>
                جمع کل واریزی‌های موفق:{" "}
                {Math.floor(totalSuccessAmount).toLocaleString("fa-IR")} تومان
              </strong>
            </div>

            <div className="admintra-filters">
              <select
                className="admintra-select"
                value={yearFilter}
                onChange={(e) => {
                  setYearFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">همه سال‌ها</option>
                {years.map((y) => (
                  <option key={y}>{y}</option>
                ))}
              </select>

              <select
                className="admintra-select"
                value={monthFilter}
                onChange={(e) => {
                  setMonthFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">همه ماه‌ها</option>
                {[
                  "فروردین",
                  "اردیبهشت",
                  "خرداد",
                  "تیر",
                  "مرداد",
                  "شهریور",
                  "مهر",
                  "آبان",
                  "آذر",
                  "دی",
                  "بهمن",
                  "اسفند",
                ].map((m, i) => (
                  <option key={i} value={String(i + 1).padStart(2, "0")}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="admintra-table-card">
            <div className="admintra-table-wrapper">
              <table className="admintra-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>شماره مالک</th>
                    <th>فروشگاه</th>
                    <th>پلن</th>
                    <th>فاکتور</th>
                    <th>نوع پرداخت</th>
                    <th>مبلغ</th>
                    <th>تخفیف</th>
                    <th>وضعیت</th>
                    <th>تاریخ</th>
                    <th>جزئیات</th>
                    <th>لینک</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTransactions.length === 0 && (
                    <tr>
                      <td colSpan="11" className="admintra-no-data">
                        هیچ نتیجه‌ای یافت نشد.
                      </td>
                    </tr>
                  )}
                  {paginatedTransactions.map((t) => (
                    <tr key={t.id}>
                      <td>{t.id}</td>
                      <td>{t.owner_phone ?? "—"}</td>
                      <td>{t.store_name}</td>
                      <td>{t.plan_name}</td>
                      <td>{t.invoice_number}</td>
                      <td>
                        <span
                          className={`admintra-paytype ${getPayTypeClass(t.payment_type)}`}
                        >
                          {getPayTypeLabel(t.payment_type)}
                        </span>
                      </td>
                      <td>{Math.floor(t.amount).toLocaleString("fa-IR")} ت</td>
                      <td>{t.discount_code ?? "_"}</td>
                      <td>
                        <span
                          className={`admintra-status ${t.success ? "admintra-status-success" : "admintra-status-failed"}`}
                        >
                          {t.success ? "موفق" : "ناموفق"}
                        </span>
                      </td>
                      <td>
                        {dayjs(t.created_at)
                          .calendar("jalali")
                          .format("YY/MM/DD HH:mm")}
                      </td>
                      <td>
                        <button
                          onClick={() => handleShowDetails(t)}
                          className="admintra-details-btn"
                          aria-label="مشاهده جزئیات"
                          title="مشاهده جزئیات"
                        >
                          <EyeIcon />
                        </button>
                      </td>
                      <td>
                        <a
                          href={`/${t.store_slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="admintra-store-link"
                        >
                          <ExternalLinkIcon />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredTransactions.length > 0 && (
              <div className="orders-pagination">
                <span className="pagination-info">
                  {toFa(filteredTransactions.length)} تراکنش — صفحه{" "}
                  {toFa(pageSafe)} از {toFa(totalPages)}
                </span>

                <div className="pagination-controls">
                  <button
                    className="page-btn"
                    disabled={pageSafe === 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    aria-label="صفحه قبل"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M15 6l-6 6 6 6" />
                    </svg>
                  </button>
                  {pageNumbers.map((n) => (
                    <button
                      key={n}
                      className={`page-btn number${n === pageSafe ? " active" : ""}`}
                      onClick={() => setCurrentPage(n)}
                    >
                      {toFa(n)}
                    </button>
                  ))}
                  <button
                    className="page-btn"
                    disabled={pageSafe === totalPages}
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    aria-label="صفحه بعد"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M9 6l6 6-6 6" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

          {modalOpen && storeDetail && (
            <div className="admintra-modal-overlay">
              <div className="admintra-modal-box">
                <div className="admintra-modal-header">
                  <h2 className="admintra-modal-title">جزئیات فروشگاه</h2>
                  <button
                    onClick={handleCloseModal}
                    className="admintra-modal-close-icon"
                    aria-label="بستن"
                    title="بستن"
                  >
                    ×
                  </button>
                </div>

                <div className="admintra-modal-body">
                  {/* بخش کلی */}
                  <div className="admintra-modal-section">
                    <div className="admintra-modal-row">
                      <span className="admintra-modal-label">نام فروشگاه</span>
                      <span className="admintra-modal-value">
                        {storeDetail.store_name}
                      </span>
                    </div>
                    {selectedTx && (
                      <div className="admintra-modal-row">
                        <span className="admintra-modal-label">روش پرداخت</span>
                        <span
                          className={`admintra-paytype ${getPayTypeClass(selectedTx.payment_type)}`}
                        >
                          {getPayTypeLabel(selectedTx.payment_type)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* بخش مالک */}
                  <div className="admintra-modal-section">
                    <div className="admintra-modal-section-title">
                      <span className="admintra-modal-section-icon">🏪</span>
                      اطلاعات مالک (صاحب فروشگاه)
                    </div>
                    <div className="admintra-modal-row">
                      <span className="admintra-modal-label">
                        نام و نام خانوادگی
                      </span>
                      <span className="admintra-modal-value">
                        {storeDetail.owner_full_name}
                      </span>
                    </div>
                    <div className="admintra-modal-row">
                      <span className="admintra-modal-label">شماره تماس</span>
                      <span className="admintra-modal-value">
                        {storeDetail.owner_phone}
                      </span>
                    </div>
                    <div className="admintra-modal-row">
                      <span className="admintra-modal-label">شناسه کاربری</span>
                      <span className="admintra-modal-value">
                        {storeDetail.owner_id}
                      </span>
                    </div>
                  </div>

                  {/* بخش سازنده */}
                  <div className="admintra-modal-section">
                    <div className="admintra-modal-section-title">
                      <span className="admintra-modal-section-icon">👤</span>
                      اطلاعات سازنده (ثبت‌کننده)
                    </div>
                    <div className="admintra-modal-row">
                      <span className="admintra-modal-label">
                        نام و نام خانوادگی
                      </span>
                      <span className="admintra-modal-value">
                        {storeDetail.creator_full_name}
                      </span>
                    </div>
                    <div className="admintra-modal-row">
                      <span className="admintra-modal-label">نقش</span>
                      <span
                        className={`admintra-role-badge ${storeDetail.creator_role === "marketer" ? "admintra-role-marketer" : "admintra-role-user"}`}
                      >
                        {storeDetail.creator_role === "marketer"
                          ? "مارکتر"
                          : "کاربر عادی"}
                      </span>
                    </div>
                    {storeDetail.creator_phone && (
                      <div className="admintra-modal-row">
                        <span className="admintra-modal-label">شماره تماس</span>
                        <span className="admintra-modal-value">
                          {storeDetail.creator_phone}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* سایر اطلاعات */}
                  <div className="admintra-modal-section">
                    <div className="admintra-modal-section-title">
                      <span className="admintra-modal-section-icon">📋</span>
                      اطلاعات فروشگاه
                    </div>
                    <div className="admintra-modal-row">
                      <span className="admintra-modal-label">نوع فروشگاه</span>
                      <span className="admintra-modal-value">
                        {storeDetail.store_type}
                      </span>
                    </div>
                    <div className="admintra-modal-row">
                      <span className="admintra-modal-label">استان</span>
                      <span className="admintra-modal-value">
                        {storeDetail.province}
                      </span>
                    </div>
                    <div className="admintra-modal-row">
                      <span className="admintra-modal-label">شهر</span>
                      <span className="admintra-modal-value">
                        {storeDetail.city}
                      </span>
                    </div>
                    <div className="admintra-modal-row">
                      <span className="admintra-modal-label">آدرس</span>
                      <span className="admintra-modal-value">
                        {storeDetail.address}
                      </span>
                    </div>
                    <div className="admintra-modal-row">
                      <span className="admintra-modal-label">تلفن ثابت</span>
                      <span className="admintra-modal-value">
                        {storeDetail.phone}
                      </span>
                    </div>
                    <div className="admintra-modal-row">
                      <span className="admintra-modal-label">موبایل</span>
                      <span className="admintra-modal-value">
                        {storeDetail.mobile}
                      </span>
                    </div>
                    <div className="admintra-modal-row">
                      <span className="admintra-modal-label">پلن</span>
                      <span className="admintra-modal-value">
                        {storeDetail.plan}
                      </span>
                    </div>
                    <div className="admintra-modal-row">
                      <span className="admintra-modal-label">تاریخ ایجاد</span>
                      <span className="admintra-modal-value">
                        {dayjs(storeDetail.created_at)
                          .calendar("jalali")
                          .format("YYYY/MM/DD HH:mm")}
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleCloseModal}
                  className="admintra-close-btn"
                >
                  بستن
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
